import { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useLoading } from '../../commonHooks/useLoading';
import { useAppDispatch } from '../../Redux/Store';
import { useFeedlotId } from '../../commonHooks/useFeedlotId';
import { CommonConstants } from '../../utils/constants/CommonConstants';
import { validateAccordingType } from '../../utils/validation';
import { loaderConstants } from '../../utils/constants/LoaderConstants';
import {
    ILoaderDetail,
    ILoaderModal,
} from '../../utils/Interface/LoaderInterface';
import {
    createLoader,
    fetchInactiveLoaderInfo,
    updateLoaderDetail,
} from '../../Redux/Actions/loaderActions';
import FullPageLoader from '../common/FullPageLoader';
import { useModalStatus } from '../../commonHooks/useModalStatus';
import { ActivateItemModal } from '../../apps/shippingTracker/modules/BuyPo/components/ActivateItemModal';
import { FmSettingsConstant } from '../../utils/constants/FmSettingsConstants';
import { LOADER_INITIAL_STATE } from '../../Redux/Constants/loaderInitialState';

const LoaderModal = ({
    showModal,
    closeModalWithChanges,
    editLoaderDetails,
    showActiveField,
}: ILoaderModal) => {
    const [loaderDetail, setLoaderDetail] =
        useState<ILoaderDetail>(editLoaderDetails);
    const [loaderDetailNameError, setLoaderDetailNameError] =
        useState<boolean>(false);
    const [inActiveLoader, setInActiveLoader] = useState<ILoaderDetail>();
    const {
        isOpen: isActivateModalOpen,
        open: openActivateModal,
        close: closeActivateModal,
    } = useModalStatus();
    const { loading, setLoading } = useLoading();
    const dispatch = useAppDispatch();
    const feedlotId = useFeedlotId();

    const validateLoaderDetails = (): boolean => {
        if (!loaderDetail.name) {
            setLoaderDetailNameError(true);
            return false;
        }
        return true;
    };

    const handleActivateLoader = async () => {
        if (feedlotId && loaderDetail.name) {
            const inactiveLoaderInfo = await dispatch(
                fetchInactiveLoaderInfo(feedlotId, loaderDetail.name.trim()),
            );
            if (inactiveLoaderInfo) {
                setInActiveLoader(inactiveLoaderInfo[0]);
                openActivateModal();
            }
        }
    };

    const handleUpdateLoaderDetail = (): void => {
        const isValid = validateLoaderDetails();
        if (feedlotId && isValid) {
            dispatch(
                updateLoaderDetail(feedlotId, loaderDetail, {
                    closeModalWithChanges,
                    setLoading,
                    handleActivateLoader,
                }),
            );
        }
    };

    const handleCreateLoader = (): void => {
        const isValid = validateLoaderDetails();
        if (feedlotId && isValid) {
            dispatch(
                createLoader(
                    feedlotId,
                    loaderDetail,
                    closeModalWithChanges,
                    setLoading,
                    handleActivateLoader,
                ),
            );
        }
    };

    const handleDetailChange = (
        fieldValue: string | number | boolean,
        fieldName: string,
        type?: string,
    ): void => {
        const isValid = validateAccordingType(type, fieldValue);
        if (isValid) {
            setLoaderDetail(prevLoaderDetail => ({
                ...prevLoaderDetail,
                [fieldName]: fieldValue,
            }));
        }
    };

    const handleCloseActivateModal = (isActivated: boolean) => {
        if (isActivated && feedlotId) {
            if (inActiveLoader) {
                const inActiveLoaderCopy = {
                    ...inActiveLoader,
                    active: true,
                };
                dispatch(
                    updateLoaderDetail(feedlotId, inActiveLoaderCopy, {
                        closeModalWithChanges,
                        setLoading,
                    }),
                );
                closeActivateModal();
            }
        } else {
            closeActivateModal();
        }
    };

    useEffect(() => {
        if (!showModal) {
            setLoaderDetail({ ...LOADER_INITIAL_STATE.createLoaderInitial });
        }
    }, [showModal]);

    return (
        <>
            <ActivateItemModal
                showModal={isActivateModalOpen}
                closeModalHandler={handleCloseActivateModal}
                itemName={loaderDetail.name?.trim()}
                item={loaderConstants.activateModalConstant}
            />
            <Modal
                className="px-2 pt-5 fm-settings-modal"
                size="lg"
                show={showModal}
                centered
                animation
                onHide={() => closeModalWithChanges(false)}
            >
                {loading && <FullPageLoader />}
                <Modal.Header closeButton className="border-bottom-0">
                    <Modal.Title className="text-dark">
                        {editLoaderDetails.name
                            ? `${loaderConstants.editLoader} ${editLoaderDetails?.name}`
                            : loaderConstants.createNewLoaders.heading}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex justify-content-between">
                        <div className="col-5 mb-4">
                            <Form.Label className="text-secondary">
                                {
                                    loaderConstants.createNewLoaders.form.name
                                        .label
                                }
                                <span className="necessaryFields">*</span>
                            </Form.Label>
                            <Form.Control
                                className={`text-secondary border-${
                                    loaderDetailNameError ? 'danger' : ''
                                }`}
                                value={loaderDetail?.name}
                                name={
                                    loaderConstants.createNewLoaders.form.name
                                        .name
                                }
                                placeholder={
                                    loaderConstants.createNewLoaders.form.name
                                        .placeholder
                                }
                                onChange={e =>
                                    handleDetailChange(
                                        e.target.value,
                                        e.target.name,
                                    )
                                }
                                maxLength={
                                    loaderConstants.createNewLoaders.form.name
                                        .maxLength
                                }
                                size="sm"
                                type="text"
                            />
                        </div>
                        {showActiveField && (
                            <div className="custom-checkbox col-5 ">
                                <input
                                    type="checkbox"
                                    checked={loaderDetail.active}
                                    className="mx-2"
                                    onChange={e =>
                                        handleDetailChange(
                                            !loaderDetail.active,
                                            'active',
                                        )
                                    }
                                />
                                <label className="mx-2">
                                    {FmSettingsConstant.active.label}
                                </label>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-top-0">
                    <Button
                        className="secondary"
                        onClick={() => closeModalWithChanges(false)}
                    >
                        {CommonConstants.en.cancel}
                    </Button>
                    <Button
                        onClick={
                            !editLoaderDetails.name
                                ? handleCreateLoader
                                : handleUpdateLoaderDetail
                        }
                    >
                        {CommonConstants.en.save}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default LoaderModal;
